import { cloneDeep } from "lodash";
import type { Observable } from "rxjs";
import { map } from "rxjs/operators";

import type {
    ILanguage,
    IPartnerUserSite,
    IPartnerUserSlim,
    IProfile,
    IResponse,
    IUserNotificationSettings,
} from "App/Modules/Models";
import { NotificationChannel, NotificationType, Permission } from "App/Modules/Models";

import type { IHttpService } from "App/Modules/Services";
import type { IProfileRepository } from "./IProfileRepository";

export class ProfileRepository implements IProfileRepository {
    public static $inject = ["mmHttpService"];

    constructor(private readonly httpService: IHttpService) {}

    public get(): Observable<IResponse<IProfile>> {
        return this.httpService.get<IProfile>("profile").pipe(
            map((response: IResponse<IProfile>) => {
                if (response.data) {
                    response.data.userPermissions.partnerPermissions =
                        response.data.userPermissions.partnerPermissions.map(
                            (pp) => (Permission as any)[pp] as Permission,
                        );
                    response.data.userPermissions.sitePermissions = response.data.userPermissions.sitePermissions.map(
                        (pp) => (Permission as any)[pp] as Permission,
                    );
                }
                return response;
            }),
        );
    }

    public patch(profilePatch: IProfile): Observable<IResponse<IProfile>> {
        return this.httpService.patch<IProfile>("profile", profilePatch).pipe(
            map((response: IResponse<IProfile>) => {
                if (response.data) {
                    response.data.userPermissions.partnerPermissions =
                        response.data.userPermissions.partnerPermissions.map(
                            (pp) => (Permission as any)[pp] as Permission,
                        );
                    response.data.userPermissions.sitePermissions = response.data.userPermissions.sitePermissions.map(
                        (pp) => (Permission as any)[pp] as Permission,
                    );
                }
                return response;
            }),
        );
    }

    public getLanguages(): Observable<IResponse<ILanguage[]>> {
        return this.httpService.get<ILanguage[]>("profile/languages");
    }

    public getNotificationSettings(): Observable<IResponse<IUserNotificationSettings[]>> {
        return this.httpService
            .get<IUserNotificationSettings[]>("profile/notificationSettings")
            .pipe(map((response) => this.enhanceNotificationSettingsResponse(response)));
    }

    public getPartners(): Observable<IResponse<IPartnerUserSlim[]>> {
        return this.httpService.get<IPartnerUserSlim[]>("profile/partners");
    }

    public getSites(): Observable<IResponse<IPartnerUserSite[]>> {
        return this.httpService.get<IPartnerUserSite[]>("profile/sites");
    }

    public patchNotificationSettings(
        notificationSettings: IUserNotificationSettings[],
    ): Observable<IResponse<IUserNotificationSettings[]>> {
        const patch = cloneDeep(notificationSettings);

        // eslint-disable-next-line no-restricted-syntax
        for (const ns of patch) {
            ns.notificationType = ns.notificationTypeName as any;
            delete ns.notificationTypeName;

            const nc = ns.notificationChannel as number;

            const notificationChannelStrings: string[] = [];
            // eslint-disable-next-line
            for (const channel in NotificationChannel) {
                if (!NotificationChannel.hasOwnProperty(channel)) {
                    continue;
                }

                const numericKey = Number(channel);
                if (Number.isNaN(numericKey)) {
                    // eslint-disable-line
                    break;
                }
                // eslint-disable-next-line no-bitwise
                if ((nc & numericKey) === numericKey) {
                    notificationChannelStrings.push(NotificationChannel[numericKey]);
                }
            }
            ns.notificationChannel = notificationChannelStrings as any;
        }

        return this.httpService
            .patch<IUserNotificationSettings[]>("profile/notificationSettings", patch)
            .pipe(map((response) => this.enhanceNotificationSettingsResponse(response)));
    }

    private enhanceNotificationSettingsResponse(
        response: IResponse<IUserNotificationSettings[]>,
    ): IResponse<IUserNotificationSettings[]> {
        if (response.data) {
            // eslint-disable-next-line no-restricted-syntax
            for (const ns of response.data) {
                ns.notificationType =
                    NotificationType[ns.notificationType as any as string as keyof typeof NotificationType];
                ns.notificationTypeName = NotificationType[ns.notificationType];
                let channel = 0;
                // eslint-disable-next-line no-restricted-syntax
                for (const nsc of ns.notificationChannel as any as string[]) {
                    channel += NotificationChannel[nsc as keyof typeof NotificationChannel] as number;
                }
                ns.notificationChannel = channel as NotificationChannel;
            }
        }
        return response;
    }
}
